import {
  Box,
  Divider,
  Flex,
  FlexProps,
  HStack,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  AdminTagCategoriesIcon,
  ArrowIcon,
  FolderPersonIcon,
  HashIcon,
  InternalAdminLogo,
  PeopleOutlineIcon,
  PersonOutlineIcon,
} from "modules/icons";
import Link from "modules/layouts/_shared/link";
import selectedNavLinkProps from "modules/messaging/left-nav/selected-nav-link-props";
import { useRouter } from "next/router";
import React, { Fragment, ReactNode, useCallback, useMemo } from "react";
import colors from "app/colors";
import { HOME_PATH } from "modules/utils/Constants";
import useStickyFilter from "modules/hooks/use-sticky-filter";
import InternalAdminOrganizationSelector from "./internal-admin-organization-selector";
import { INTERNAL_ADMIN_LEFT_NAV_STICKY_HEADER_Z_INDEX } from "modules/providers/chakra-provider/chakra-portal-stack";

interface IProps extends FlexProps {}

export const InternalAdminLeftNav = (props: IProps) => {
  const { pathname, query, push } = useRouter();

  const organizationId = useMemo(() => query.organizationId, [query]);

  const onOrganizationSelected = useCallback(
    (organizationId: string) => {
      push(`/internal-admin/users?organizationId=${organizationId}`);
    },
    [push],
  );

  const organizationsSelected = useMemo(
    () => pathname.startsWith("/internal-admin/organizations"),
    [pathname],
  );

  const usersSelected = useMemo(
    () => pathname.startsWith("/internal-admin/users"),
    [pathname],
  );

  const groupsSelected = useMemo(
    () => pathname.startsWith("/internal-admin/groups"),
    [pathname],
  );

  const tagsSelected = useMemo(
    () => pathname.startsWith("/internal-admin/tags"),
    [pathname],
  );

  const tagCategoriesSelected = useMemo(
    () => pathname.startsWith("/internal-admin/tag-categories"),
    [pathname],
  );

  const userAttributesCategoriesSelected = useMemo(
    () => pathname === "/internal-admin/user-attributes-categories",
    [pathname],
  );

  const { url: homeUrl } = useStickyFilter({
    path: HOME_PATH,
  });

  const children = useMemo(() => {
    const links: JSX.Element[] = [];
    links.push(
      <NavLink
        key="left-nav-organizations"
        icon={<PersonOutlineIcon boxSize={5} fill={colors.grayPrimary} />}
        title="All Organizations"
        href="/internal-admin/organizations"
        selected={organizationsSelected}
      />,
    );
    links.push(
      <Divider
        key="left-nav-divider-1"
        mx={4}
        pt={4}
        borderColor={colors.gray50}
      />,
    );
    links.push(
      <Box w="full">
        <InternalAdminOrganizationSelector
          onOrganizationSelected={onOrganizationSelected}
        />
      </Box>,
    );
    links.push(
      <NavLink
        key="left-nav-users"
        icon={<PersonOutlineIcon boxSize={5} fill={colors.grayPrimary} />}
        title="Users"
        href={`/internal-admin/users?organizationId=${organizationId}`}
        selected={usersSelected}
        isDisabled={!organizationId}
      />,
    );
    links.push(
      <NavLink
        key="left-nav-groups"
        icon={<PeopleOutlineIcon boxSize={5} fill={colors.grayPrimary} />}
        title="Groups"
        href={`/internal-admin/groups?organizationId=${organizationId}`}
        selected={groupsSelected}
        isDisabled={!organizationId}
      />,
    );
    links.push(
      <NavLink
        key="left-nav-tags"
        icon={<HashIcon boxSize={5} fill={colors.grayPrimary} />}
        title="Tags"
        href={`/internal-admin/tags?organizationId=${organizationId}`}
        selected={tagsSelected}
        isDisabled={!organizationId}
      />,
    );
    links.push(
      <NavLink
        key="left-nav-tag-categories"
        icon={<AdminTagCategoriesIcon fill={colors.grayPrimary} boxSize={5} />}
        title="Tag Categories"
        href={`/internal-admin/tag-categories?organizationId=${organizationId}`}
        selected={tagCategoriesSelected}
        isDisabled={!organizationId}
      />,
    );
    links.push(<NavDivider key="left-nav-divider-2" />);
    links.push(
      <NavLink
        key="user-attributes-categories"
        icon={<FolderPersonIcon boxSize={4} fill={colors.grayPrimary} />}
        title="User Attribute Categories"
        href={`/internal-admin/user-attributes-categories?organizationId=${organizationId}`}
        selected={userAttributesCategoriesSelected}
        isDisabled={!organizationId}
      />,
    );
    links.push(<NavDivider key="left-nav-divider-3" />);
    links.push(
      <NavLink
        key="left-nav-tag-back"
        icon={<ArrowIcon boxSize={5} fill={colors.grayPrimary} />}
        title="Back to Synco"
        href={homeUrl}
      />,
    );
    return links;
  }, [
    groupsSelected,
    homeUrl,
    onOrganizationSelected,
    organizationId,
    organizationsSelected,
    tagCategoriesSelected,
    tagsSelected,
    userAttributesCategoriesSelected,
    usersSelected,
  ]);

  return (
    <Flex
      bg={colors.blackTertiary}
      as="nav"
      overflowY="auto"
      height="full"
      direction="column"
      justify="space-between"
      {...props}
      color={colors.grayPrimary}
    >
      <Stack spacing={0} pb={4} pt={4}>
        <Flex
          position="sticky"
          top={0}
          pl={4}
          pr={4}
          py={3}
          align="center"
          zIndex={INTERNAL_ADMIN_LEFT_NAV_STICKY_HEADER_Z_INDEX}
          bg={colors.blackTertiary}
        >
          <HStack spacing={2} pl={2} pb={3}>
            <InternalAdminLogo fill={colors.white} />
          </HStack>
        </Flex>
        <VStack spacing={2} px={4} alignItems="flex-start">
          {children.map((child, index) => (
            <Fragment key={`nav-child-${index}`}>{child}</Fragment>
          ))}
        </VStack>
      </Stack>
    </Flex>
  );
};

const NavDivider = () => <Divider mx={4} borderColor={colors.gray50} />;

const NavLink = ({
  icon,
  title,
  href,
  selected,
  isDisabled,
}: {
  icon: ReactNode;
  title: string;
  href: string;
  selected?: boolean;
  isDisabled?: boolean;
}) => {
  if (isDisabled) {
    return (
      <HStack w="full" pl={5} pr={1} py={1} opacity={0.4}>
        {icon}
        <Text flex={1} color={colors.grayPrimary}>
          {title}
        </Text>
      </HStack>
    );
  }

  return (
    <Link
      href={href}
      w="full"
      pl={5}
      pr={1}
      py={1}
      {...(selected && selectedNavLinkProps)}
    >
      <HStack>
        {icon}
        <Text flex={1}>{title}</Text>
      </HStack>
    </Link>
  );
};
